import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaCreditCard, FaCheckCircle, FaExclamationTriangle, FaSpinner, FaTimes } from 'react-icons/fa';
import axios from 'axios';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
  hidePostalCode: false,
};

const UpdatePaymentMethodModal = ({ isOpen, onClose, currentPaymentMethod, onSuccess, hasPendingPayment = false }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!cardComplete) {
      setError(t('subscription.payment.errors.incompleteCard'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Créer le PaymentMethod avec Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        setError(stripeError.message);
        setIsLoading(false);
        return;
      }

      // Envoyer le PaymentMethod ID au backend
      const response = await axios.post('/payment/payment-method/update', {
        payment_method_id: paymentMethod.id
      });

      if (response.data.success) {
        // Si paiement en attente mais le retry a échoué, afficher l'erreur
        if (hasPendingPayment && response.data.retry_success === false) {
          setError(response.data.retry_message || t('subscription.payment.errors.paymentRetryFailed'));
          setIsLoading(false);
          return;
        }

        setSuccess(true);
        cardElement.clear();
        
        // Notifier le parent du succès
        setTimeout(() => {
          onSuccess?.();
          handleClose();
        }, 2000);
      } else {
        setError(response.data.error || t('subscription.payment.errors.updateFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('subscription.payment.errors.general'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      setSuccess(false);
      setCardComplete(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            {t('subscription.payment.modal.title')}
          </h3>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Carte actuelle */}
          {currentPaymentMethod && !success && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <FaCreditCard className="text-blue-600 mr-3 text-xl" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    {t('subscription.payment.currentCard.title')}
                  </p>
                  <p className="text-sm text-blue-700">
                    {currentPaymentMethod.brand} •••• {currentPaymentMethod.last4}
                    {currentPaymentMethod.exp_month && currentPaymentMethod.exp_year && (
                      <span className="ml-2">
                        {t('subscription.payment.currentCard.expires')} {String(currentPaymentMethod.exp_month).padStart(2, '0')}/{currentPaymentMethod.exp_year}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Message de succès */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-3 text-2xl" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    {hasPendingPayment ? t('subscription.payment.success.titleWithPayment') : t('subscription.payment.success.title')}
                  </p>
                  <p className="text-sm text-green-700">
                    {hasPendingPayment ? t('subscription.payment.success.messageWithPayment') : t('subscription.payment.success.message')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Formulaire de nouvelle carte */}
          {!success && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('subscription.payment.form.newCard')}
                </label>
                <div className="border border-gray-300 rounded-lg p-3 bg-white">
                  <CardElement 
                    options={CARD_ELEMENT_OPTIONS}
                    onChange={handleCardChange}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {t('subscription.payment.form.secureInfo')}
                </p>
              </div>

              {/* Message d'erreur */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <FaExclamationTriangle className="text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {/* Informations importantes */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {t('subscription.payment.info.title')}
                </h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• {t('subscription.payment.info.point1')}</li>
                  <li>• {hasPendingPayment ? t('subscription.payment.info.point2WithPending') : t('subscription.payment.info.point2')}</li>
                  <li>• {t('subscription.payment.info.point3')}</li>
                </ul>
              </div>

              {/* Boutons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 font-medium"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !stripe || !cardComplete}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isLoading ? (
                    <>
                      <FaSpinner className="animate-spin mr-2" />
                      {t('subscription.payment.form.processing')}
                    </>
                  ) : (
                    <>
                      <FaCreditCard className="mr-2" />
                      {t('subscription.payment.form.updateCard')}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Message de sécurité Stripe */}
          <div className="flex items-center justify-center text-xs text-gray-500 pt-2">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            {t('subscription.payment.stripe.secure')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdatePaymentMethodModal;
